import { call, delay, getContext, put, select, spawn, takeEvery } from 'redux-saga/effects';
import storage from 'store2';

import { getUser, loginWithToken } from '@skippay/core/firebase/auth';
import { captureException } from '@skippay/core/global/errorTracking';
import { getUserFirebaseUid, setUserFirebaseUid } from '@skippay/core/modules/user';
import { rethrow } from '@skippay/core/utils/fp';

import AccountFirebaseTokenQuery from 'queries/AccountFirebaseTokenQuery.graphql';
import MyProfileQuery from 'queries/MyProfileQuery.graphql';
import { identify } from 'utils/exponeaUtils';
import { jarabotClient } from 'utils/jarabotClient';

import { handleAuthorizedChannelTask } from './handleAccessToken';
import { LOGIN, login, updateLoginStatus, userLoggedIn } from '../actions';
import { getTokenData, getValidAccessToken } from '../utils/accessTokenUtils';

// https://firebase.google.com/docs/reference/js/auth#autherrorcodes
const TRANSIENT_FIREBASE_ERRORS = [
	'auth/network-request-failed',
	'auth/timeout',
	'auth/too-many-requests',
];

const FIREBASE_MAX_RETRIES = 3;
const FIREBASE_BASE_DELAY_MS = 1000;

function* getCurrentUser() {
	const apolloClient = yield getContext('apolloClient');
	const apolloResponse = yield apolloClient.query({
		fetchPolicy: 'cache-first',
		query: MyProfileQuery,
	});

	return apolloResponse.data?.myProfile;
}

function* getFirebaseToken() {
	const apolloClient = yield getContext('apolloClient');
	const apolloResponse = yield apolloClient.query({
		fetchPolicy: 'network-only',
		query: AccountFirebaseTokenQuery,
	});

	return new Promise((resolve, reject) => {
		if (apolloResponse.errors) {
			reject(apolloResponse.errors);
		} else {
			if (apolloResponse.data?.accountFirebaseToken?.customToken) {
				resolve(apolloResponse.data.accountFirebaseToken.customToken);
			}

			reject(new Error('No custom token'));
		}
	});
}

function* handleFirebaseLoginError(e, attempt) {
	const isTransient = TRANSIENT_FIREBASE_ERRORS.includes(e.code);
	const hasRetriesLeft = attempt < FIREBASE_MAX_RETRIES - 1;

	if (isTransient && hasRetriesLeft) {
		const backoffDelay = FIREBASE_BASE_DELAY_MS * 2 ** attempt;
		yield delay(backoffDelay);
	} else {
		if (process.env.NODE_ENV === 'production' && e.code !== 'auth/invalid-custom-token') {
			captureException(new Error('Firebase login error'), {
				extra: {
					originalError: e,
					message: e.message,
					code: e.code,
					attempt: attempt + 1,
					maxRetries: FIREBASE_MAX_RETRIES,
				},
				originalException: e,
			});
		}
		rethrow(e);
	}
}

function* firebaseLoginTask() {
	if (process.env.NODE_ENV === `development` && !process.env.NO_MOCK && !storage.get(`NO_MOCK`)) {
		return;
	}

	for (let attempt = 0; attempt < FIREBASE_MAX_RETRIES; attempt += 1) {
		try {
			const firebaseUser = getUser();

			const savedFirebaseUser = yield select(getUserFirebaseUid);

			if (!firebaseUser) {
				const newCustomToken = yield call(getFirebaseToken);

				yield call(loginWithToken, newCustomToken);

				// defer API call so NetworkInterceptor has time to attach error handlers
				yield delay(5);

				const newSignedUser = getUser();

				if (newSignedUser) {
					yield put(setUserFirebaseUid(newSignedUser.uid));
				}
			}

			if (!savedFirebaseUser && firebaseUser) {
				yield put(setUserFirebaseUid(firebaseUser.uid));
			}

			return;
		} catch (e) {
			yield call(handleFirebaseLoginError, e, attempt);
		}
	}
}

function* loginTask(action) {
	const { payload: tokenData } = action;

	yield spawn(handleAuthorizedChannelTask, tokenData);
	if (!tokenData.fullAccess) return;

	yield put(updateLoginStatus(true));

	// defer API call so NetworkInterceptor has time to attach error handlers
	yield delay(5);
	const user = yield call(getCurrentUser);

	if (user) {
		identify(user);
		jarabotClient.setUser(user.id, {
			name: user.name.firstName,
			country_code: user.country,
		});
		jarabotClient.setCustomAttributes({
			'customer.kyc.isOutdated': user.kyc?.isOutdated,
			'customer.amlLegalReview.isRequested': user.amlLegalReview?.isRequested,
		});
		yield spawn(firebaseLoginTask);
	}

	yield put(userLoggedIn(user));
}

export default function* bootstrapLogin() {
	yield takeEvery(LOGIN, loginTask);

	// if user is logged in, perform side-effects bind to login
	const accessToken = yield call(getValidAccessToken);
	if (accessToken) {
		const tokenData = yield call(getTokenData);
		yield put(login(tokenData));
	}
}
